import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { FaThumbtack, FaMicrophone, FaSpinner } from "react-icons/fa";
import NodeFooter from "./NodeFooter";
import SpeakerIcon from "./SpeakerIcon";
import DownloadAudioIcon from "./DownloadAudioIcon";
import ModelSelector from "./ModelSelector";
import LooreTooltip from "./LooreTooltip";
import NodeForm from "./NodeForm";
import ProposalInline, { hasProposalSections, hasShareBlocks, splitProposalText } from "./ProposalInline";
import SemanticNeighbors from "./SemanticNeighbors";
import { useUser } from "../contexts/UserContext";
import { useToast } from "../contexts/ToastContext";
import { useAsyncTaskPolling } from "../hooks/useAsyncTaskPolling";
import { useLlmTextStream } from "../hooks/useSSE";
import { peekPrefetchedNode, takePrefetchedNode } from "../hooks/useNodePrefetch";
import api from "../api";
import { useCheckboxToggle, useTaskInsert } from "../utils/markdown";
import { contextAllowsAi, isAiUsageRefusedError } from "../utils/aiUsage";
import NodeFormModal from "./NodeFormModal";
import Bubble from "./Bubble";
import BubbleKebabMenu from "./BubbleKebabMenu";
import QuotedContent from "./QuotedContent";
import FeedPicks from "./FeedPicks";
import ToolActionsTaken from "./ToolActionsTaken";
import { ReadWindowLine, ReadReplyTail, ReadCancelledLine } from "./ReadReply";

import DeleteConfirmDialog from "./DeleteConfirmDialog";


// Glean (#435) is the Community Archive read under its user-facing name.
// The Glean button's tooltip before the thread's first gleaning, and
// after it (another pass over the day).
const GLEAN_AGAIN_TITLE = "Another pass over today's tweets, against everything in this thread so far "
  + "— your marks on the earlier picks included.";
// Wording: Peter, 2026-10-13. The reflection thread's top-right button
// uses the same text.
const GLEAN_TITLE = "Loore reads today's Community Archive tweets and shows you those relevant "
  + "for this thread.";
export const GLEAN_MENU_LABEL = 'Glean for this reflection';
export const GLEANINGS_TITLE = "Today's gleanings";

// Recursive component to render children nodes.
function RenderChildTree({ nodes, onBubbleClick, buildActions }) {
  return (
    <div>
      {nodes.map((child, index) => {
        const containerStyle = (nodes.length > 1)
          ? { marginLeft: "20px", paddingLeft: "10px", borderLeft: "2px solid var(--border)" }
          : { marginLeft: "0px" };

        return (
          <div key={child.id}>
            <div style={containerStyle}>
              <Bubble
                node={child}
                onClick={onBubbleClick}
                leftAlign={true}
                actions={buildActions ? buildActions(child) : null}
              />
              {child.children && child.children.length > 0 &&
                <RenderChildTree nodes={child.children} onBubbleClick={onBubbleClick} buildActions={buildActions} />
              }
            </div>
            {index < nodes.length - 1 && (
              <hr style={{ borderColor: "var(--border)", marginLeft: containerStyle.marginLeft }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// A reply's text while it is written (#367), as it can be shown before
// the final render: quote markers become cards only once the reply is
// complete (their quotes are resolved from the stored content), and share
// fences only turn into a card then — until then the piece shows as text.
const SHARE_FENCE_LINE_RE = /^:::(?:share(?:[ \t]+[A-Za-z]+)?)?[ \t]*\r?$/i;
const partialReplyText = (text) => (text || '')
  .replace(/\{quote(?:_ext)?:\d+\}/g, '')
  .split('\n')
  .filter(line => !SHARE_FENCE_LINE_RE.test(line))
  .join('\n');

// History state for a move from a node to its own pending reply: the
// entry before this one is the reply's parent. A reply that fails then
// goes back to it instead of adding it to the history a second time.
const FROM_PARENT = { fromParent: true };

// A glean's reply (#435), pending or done: its live marker, a glean-again
// marker, or the read prompt right above it.
const isGleaningNode = (node) => {
  const meta = Array.isArray(node?.tool_calls_meta) ? node.tool_calls_meta : [];
  const parent = node?.ancestors?.[node.ancestors.length - 1];
  return !!node?.read_reply
    || meta.some(tc => ['_live', '_read'].includes(tc?.name))
    || ['read', 'read_thread'].includes(parent?.prompt_key);
};

// The browser tab's title for a node: its first line, or a state word
// while an AI reply is still being generated.
const tabTitleFor = (node) => {
  const pending = (node?.node_type === 'llm' || !!node?.llm_model)
    && (node?.llm_task_status === 'pending' || node?.llm_task_status === 'processing');
  if (pending) {
    const meta = Array.isArray(node?.tool_calls_meta) ? node.tool_calls_meta : [];
    const batch = meta.some(tc => tc?.name === '_batch'
                                  && ['submitted', 'cancelling'].includes(tc.status));
    const glean = isGleaningNode(node);
    return `${batch ? 'Processing' : (glean ? 'Gleaning' : 'Thinking')}… — Loore`;
  }
  const firstLine = (node?.content || '')
    .trim().split('\n')[0].replace(/^[#>\s]+/, '').slice(0, 120);
  return firstLine ? `${firstLine} — Loore` : 'Loore';
};

// `nodeId`, `openNode` and `moving` come from NodeDetailWrapper: the id is
// the node this page shows, which stays put while the address has moved on
// to a node that is still loading; `openNode(id, go)` fetches a node before
// `go` changes the address (useNodePrefetch); `moving` is true while another
// node loads, and the billed buttons wait (a second press would start a
// second reply or read).
function NodeDetail({ nodeId: id, openNode, moving }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useUser();
  const { addToast } = useToast();
  const craftMode = !!currentUser?.craft_mode;
  // Opened from another node: its data was fetched before this page opened
  // (useNodePrefetch), so the page renders at once instead of loading.
  const [node, setNode] = useState(() => peekPrefetchedNode(id));
  const [loading, setLoading] = useState(node == null);
  const [error, setError] = useState("");
  const [showEditOverlay, setShowEditOverlay] = useState(false);
  const [selectedModel, setSelectedModel] = useState(currentUser?.preferred_model || null);
  // The Read button's own model: reads run only on the read models
  // (#355), so it never shares the LLM Response picker's choice. Null
  // until its picker loads; the backend then picks (resolve_read_model).
  const [readModel, setReadModel] = useState(null);
  const [llmTaskNodeId, setLlmTaskNodeId] = useState(null);
  // True from the click until POST /nodes/:id/llm answers: the spinner is
  // otherwise driven by the returned node id, so the round trip (a cold
  // dev server can take seconds) showed a button that ignored the click.
  const [llmRequesting, setLlmRequesting] = useState(false);
  const [quotes, setQuotes] = useState({});
  const [externalQuotes, setExternalQuotes] = useState({});
  // Bumped when another bubble in the thread is edited or deleted: the
  // focal content (and so its quote markers) is unchanged, but a quoted
  // node may be that bubble, so the resolved quotes must be refetched.
  const [quotesVersion, setQuotesVersion] = useState(0);
  const [pinLoading, setPinLoading] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [readLoading, setReadLoading] = useState(false);
  const [toolActionsExpanded, setToolActionsExpanded] = useState(false);
  const [showPromptEditConfirm, setShowPromptEditConfirm] = useState(false);
  // Per-bubble action targets. The kebab on any rendered Bubble (focal,
  // ancestor, child) sets exactly one of these via setExclusiveTarget.
  const [replyTarget, setReplyTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // A confirmed delete that would leave a text/voice session with only
  // its system prompt: { targetId, withDescendants }. The follow-up
  // dialog asks whether to delete the prompt as well.
  const [pendingPromptDelete, setPendingPromptDelete] = useState(null);
  const setExclusiveTarget = useCallback((slot, value) => {
    setReplyTarget(slot === 'reply' ? value : null);
    setEditTarget(slot === 'edit' ? value : null);
    setDeleteTarget(slot === 'delete' ? value : null);
  }, []);
  // autoGenerate is shared across the whole text-mode experience — the
  // NodeDetailWrapper uses `key={id}` which remounts NodeDetail on every
  // node navigation, so local useState would reset the toggle. Persist to
  // localStorage so it survives the remount.
  const [autoGenerate, setAutoGenerateState] = useState(() => {
    const stored = localStorage.getItem('loore_auto_generate');
    return stored === null ? true : stored === 'true';
  });
  const setAutoGenerate = useCallback((next) => {
    setAutoGenerateState(prev => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      localStorage.setItem('loore_auto_generate', String(resolved));
      return resolved;
    });
  }, []);
  const highlightedNodeRef = useRef(null);

  // `autoGenerate` is the single source of truth. Defaults to true on
  // a fresh install (see useState initializer) and persists across
  // remounts via localStorage. The toggle is only visible in Craft
  // mode, but the stored preference governs behavior in both modes so
  // the UI state always matches observed behavior.
  //
  // PUBLIC threads (#228) are the exception: generation there is always a
  // deliberate act — auto-generate is forced off (and its toggle hidden),
  // and the explicit LLM Response bar shows for the node's owner instead.
  const isPublicThread = node?.privacy_level === 'public';
  const autoGenerateActive = isPublicThread ? false : autoGenerate;

  // LLM completion polling - enabled automatically when llmTaskNodeId is set
  // Batch stage ({ca_tweets}): the synchronous part is done and the turn
  // is queued at the provider (minutes, up to 24 h). Poll slowly and
  // don't time out at the hook's 30-minute default.
  const batchMeta = Array.isArray(node?.tool_calls_meta)
    ? node.tool_calls_meta.find(tc => tc?.name === '_batch'
                                     && ['submitted', 'cancelling'].includes(tc.status))
    : null;
  // (Only the pending node's own meta counts here: on the parent page the
  // batch stage triggers a navigation to that node — see the completion
  // effect — so the slow cadence is needed there, not before.)
  const isBatchWait = !!batchMeta;
  const {
    status: llmStatus,
    data: llmData,
    error: llmError
  } = useAsyncTaskPolling(
    llmTaskNodeId ? `/nodes/${llmTaskNodeId}/llm-status` : null,
    {
      enabled: !!llmTaskNodeId,  // Auto-start when llmTaskNodeId is set
      interval: isBatchWait ? 15000 : 2000,
      maxDuration: isBatchWait ? 25 * 60 * 60 * 1000 : 30 * 60 * 1000,
      // A finished Read reply counts as opened (FeedRender.opened_at) only
      // for a poll from a visible tab; this page polls in the background
      // too (batch reads take up to 25 h).
      reportVisible: true,
    }
  );

  // #367: a pending reply's text while the model writes it. The node
  // fetch carries the text so far; the stream brings the rest. (A batch
  // read is never streamed.)
  const replyStreaming = !!node && (node.node_type === 'llm' || !!node.llm_model)
    && (node.llm_task_status === 'pending' || node.llm_task_status === 'processing')
    && !isBatchWait;
  const { text: streamText } = useLlmTextStream(
    replyStreaming ? node.id : null,
    { enabled: replyStreaming, initialText: node?.streaming_content || '' },
  );

  // Loads once per node: NodeDetailWrapper keys this component by id, so
  // `loading` starts true unless the node came with the click.
  useEffect(() => {
    setError("");
    setQuotes({}); // Reset quotes when node changes
    setExternalQuotes({});
    // Opened from another node: the request made before this page opened
    // (settled, or still in flight after a slow answer), not a second one.
    (takePrefetchedNode(id) || api.get(`/nodes/${id}`))
      .then((response) => {
        setNode(response.data);
        setLoading(false);
        // /node/<id> is server-rendered: a private node arrives in the
        // "Not found" shell (404 for private-or-missing alike, so ids can't
        // be probed), so the tab title has to be set here once the
        // logged-in fetch succeeds — same first-line rule PublicThreadPage
        // uses. Reset on unmount below.
        document.title = tabTitleFor(response.data);
        // Human-readable address (#228): when the node has a permalink and
        // we arrived via /node/<id>, show the pretty URL instead. Display
        // only — router state is untouched, and revisiting the pretty URL
        // resolves through the permalink route.
        if (response.data.permalink
            && window.location.pathname === `/node/${id}`) {
          window.history.replaceState(null, '', response.data.permalink);
        }
      })
      .catch((err) => {
        console.error(err);
        // 404 covers missing, deleted and not-shared-with-you alike (the
        // backend never says which), so the message has to as well.
        if (err.response && (err.response.status === 404 || err.response.status === 403)) {
          setError("This node doesn't exist, was deleted, or isn't shared with you.");
        } else {
          setError("Error fetching node details.");
        }
        setLoading(false);
      });
    return () => {
      document.title = 'Loore';
    };
  }, [id]);

  // Keep the tab title with the content: a pending reply loads as its
  // placeholder text and is patched in place when the batch lands, so
  // the title set on fetch would otherwise stay "[LLM response generation
  // pending...]" for the finished reply.
  useEffect(() => {
    if (node) document.title = tabTitleFor(node);
  }, [node]);

  // Fetch quote data when node loads (if content contains {quote:ID} /
  // {quote_ext:ID} placeholders). Keyed on the markers themselves, not the
  // node object: a checklist toggle or an in-place patch replaces the
  // object without changing which quotes the content holds (#321).
  const quoteMarkers = (node?.content?.match(/\{quote(?:_ext)?:\d+\}/g) || []).join(',');
  useEffect(() => {
    if (!quoteMarkers) return;

    // Fetch quotes for this node
    api
      .get(`/nodes/${id}/resolve-quotes`)
      .then((response) => {
        if (response.data.has_quotes && response.data.quotes) {
          setQuotes(response.data.quotes);
        }
        if (response.data.has_quotes && response.data.external_quotes) {
          setExternalQuotes(response.data.external_quotes);
        }
      })
      .catch((err) => {
        console.error("Error fetching quotes:", err);
        // Don't show error to user - quotes will just not render
      });
  }, [id, quoteMarkers, quotesVersion]);

  // Scroll to the focal node once, after its thread loads. Keyed on the
  // id: checklist toggles, the "+" insert, edits and in-place LLM patches
  // all replace the node object, and each of those used to scroll the page
  // back to the top of the focal node (#321).
  const focalId = node?.id;
  const scrolledToIdRef = useRef(null);
  useEffect(() => {
    if (loading || focalId == null || !highlightedNodeRef.current) return;
    if (scrolledToIdRef.current === focalId) return;
    scrolledToIdRef.current = focalId;
    highlightedNodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, focalId]);

  // If we arrived with ?awaitLlm=NID (e.g. from WritePage), pick up the
  // pending LLM task and poll it here.
  // Re-runs when `id` changes so internal navigations to another node
  // with ?awaitLlm= also take effect (react-router reuses the component).
  // An entry that arrives with its reply pending (?awaitLlm= another
  // node) goes on to that reply at once (#367): it shows the model
  // thinking, then the text as it is written. The entry keeps its own
  // history step (the param is dropped first, so a back press lands on
  // the entry and stays there). Handled once: StrictMode runs this twice
  // in development, and a second hand-off would push the reply again.
  const awaitLlmHandledRef = useRef(null);
  useEffect(() => {
    const awaitLlm = searchParams.get('awaitLlm');
    if (awaitLlm && awaitLlmHandledRef.current !== awaitLlm) {
      awaitLlmHandledRef.current = awaitLlm;
      const next = new URLSearchParams(searchParams);
      next.delete('awaitLlm');
      setSearchParams(next, { replace: true, state: location.state });
      if (String(awaitLlm) === String(id)) {
        setLlmTaskNodeId(parseInt(awaitLlm, 10));
      } else {
        navigate(`/node/${awaitLlm}?awaitLlm=${awaitLlm}`,
                 { state: FROM_PARENT });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Opening a pending LLM node directly (the batch wait parks the view
  // there) resumes polling on it, so the reply patches in when it lands.
  useEffect(() => {
    if (!node || llmTaskNodeId) return;
    const pending = (node.node_type === 'llm' || !!node.llm_model)
      && (node.llm_task_status === 'pending' || node.llm_task_status === 'processing');
    if (pending && String(node.id) === String(id)) {
      setLlmTaskNodeId(node.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id, node?.llm_task_status]);

  // editTarget drives Edit's two-path branch: prompt-rooted nodes show
  // a confirmation first; everything else opens the edit overlay
  // directly. Both paths route through editTarget.id, so focal and
  // non-focal edits share one code path.
  useEffect(() => {
    if (!editTarget) return;
    if (editTarget.context_artifacts?.prompt) {
      setShowPromptEditConfirm(true);
    } else {
      setShowEditOverlay(true);
    }
  }, [editTarget]);

  // Handle LLM completion. Both branches require llmTaskNodeId: after it
  // is cleared, this effect re-runs once (llmTaskNodeId is a dep) while
  // llmStatus/llmData are still stale — the polling hook resets them a
  // render later — and without the guard that stale pass acts twice
  // (duplicate toast / duplicate navigation). While another node loads
  // (`moving`), this page may be on its way out (a back press, a click):
  // it acts on the result only if it stays (the move is undone).
  useEffect(() => {
    if (!llmTaskNodeId || moving) return;
    if (llmStatus === 'completed' && llmData) {
      // Prefer the id of the node returned in payload; fall back to the
      // polled node id (llmTaskNodeId).
      const completedId = llmData.node?.id || llmTaskNodeId;
      // Within-turn retrieval (#158): an interim node carries a
      // continuation_node_id pointing at the node that holds (or will hold)
      // the answer. Follow the chain — view the continuation and keep polling
      // it — so the interim retrieval step renders as its own bubble and the
      // final answer arrives in the next. Repeats for each retrieval round.
      if (llmData.continuation_node_id) {
        const contId = llmData.continuation_node_id;
        // This turn's own text is done: shown as finished, should the
        // page stay (a back press while the continuation loads).
        if (String(completedId) === String(id)) {
          setNode(prev => prev ? {
            ...prev,
            content: llmData.content ?? prev.content,
            tool_calls_meta: llmData.tool_calls_meta ?? prev.tool_calls_meta,
            llm_task_status: 'completed',
          } : prev);
        }
        // Navigate WITH ?awaitLlm so polling re-establishes on the
        // continuation node — NodeDetail remounts on :id change, so bare
        // llmTaskNodeId state would be lost (this matches WritePage's
        // handoff). The awaitLlm effect picks it up after the remount.
        setLlmTaskNodeId(null);
        navigate(`/node/${contId}?awaitLlm=${contId}`, { state: FROM_PARENT });
        return;
      }
      if (completedId && String(completedId) === String(id)) {
        // We're already viewing the pending LLM node — patch its state
        // in place so the rendered content switches from "Thinking…" to
        // the final response without a navigation jump.
        setNode(prev => prev ? {
          ...prev,
          content: llmData.content ?? prev.content,
          tool_calls_meta: llmData.tool_calls_meta ?? prev.tool_calls_meta,
          feed_picks_count: llmData.feed_picks_count ?? prev.feed_picks_count,
          llm_task_status: 'completed',
        } : prev);
        // A finished gleaning also has its render now (which day it read,
        // how many tweets: the window line and the empty day's count),
        // which the poll does not carry: fetch the node once more.
        if (isGleaningNode(node)) {
          api.get(`/nodes/${id}`)
            .then((res) => {
              if (res.data?.llm_task_status === 'completed') setNode(res.data);
            })
            .catch(() => {});
        }
      } else if (completedId) {
        navigate(`/node/${completedId}`);
      }
      setLlmTaskNodeId(null);
    } else if (llmData?.stage === 'batch'
               && String(llmTaskNodeId) !== String(id)) {
      // The turn is queued in a provider batch: the generate button's job
      // is done. Hand the wait to the pending node itself (it shows
      // "Processing" and keeps polling slowly via the effect above).
      setLlmTaskNodeId(null);
      navigate(`/node/${llmTaskNodeId}`);
    } else if (llmStatus === 'cancelled') {
      // A read withdrawn before it ran (the spend cap was reached while
      // it was queued): nothing was billed. The node's text says only
      // that it was cancelled; the error, sent to the owner only, says
      // why, here as a toast and under the text (ReadCancelledLine).
      addToast(llmData?.error || 'Read cancelled', 8000);
      if (String(llmTaskNodeId) === String(id)) {
        setNode(prev => prev ? {
          ...prev,
          content: llmData?.content ?? prev.content,
          tool_calls_meta: llmData?.tool_calls_meta ?? prev.tool_calls_meta,
          llm_task_status: 'cancelled',
          llm_task_error: llmData?.error ?? prev.llm_task_error,
        } : prev);
      }
      setLlmTaskNodeId(null);
    } else if (llmStatus === 'failed') {
      // Toast, never setError — setError replaces the entire thread view
      // with the raw failure text, hiding the thread and the inline form.
      addToast(llmError || 'LLM response generation failed', 8000);
      // awaitLlm flows can park the view on the pending LLM node itself;
      // on failure that's an empty dead node — hop to its parent (the
      // user's entry). Arrived from the parent: go back to it. Otherwise
      // replace the dead history entry, so a back press walks real nodes.
      if (String(llmTaskNodeId) === String(id)) {
        const parent = node?.ancestors?.[node.ancestors.length - 1];
        if (parent && !parent.deleted) {
          if (location.state?.fromParent) {
            navigate(-1);
          } else {
            navigate(`/node/${parent.id}`, { replace: true });
          }
        }
      }
      setLlmTaskNodeId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmStatus, llmData, llmError, navigate, id, llmTaskNodeId, moving]);

  // While the reply this page shows is pending, its tool_calls_meta
  // follows the poll (#386). A read is fetched as soon as it starts,
  // before the worker has submitted its batch, and only the poll then
  // reports the "_batch" entry. With it on the node the page says
  // "Processing…", polls at the batch cadence (15 s, for up to 25 h)
  // and closes the text stream (a batch is never streamed). Without it
  // the page said "Thinking…" until a refresh, and its 30-minute poll
  // could end long before the batch did.
  const polledMeta = llmData?.tool_calls_meta;
  useEffect(() => {
    if (!llmTaskNodeId || String(llmTaskNodeId) !== String(id)) return;
    if (llmStatus !== 'pending' && llmStatus !== 'processing') return;
    if (!Array.isArray(polledMeta)) return;
    setNode(prev => (
      prev && JSON.stringify(prev.tool_calls_meta) !== JSON.stringify(polledMeta)
        ? { ...prev, tool_calls_meta: polledMeta }
        : prev
    ));
  }, [llmTaskNodeId, id, llmStatus, polledMeta]);

  const getNodeContent = useCallback(() => node?.content, [node]);
  const setNodeContent = useCallback((newContent) => setNode(prev => ({ ...prev, content: newContent })), []);
  const saveNodeContent = useCallback((newContent) => api.put(`/nodes/${id}`, { content: newContent }), [id]);
  const handleCheckboxToggle = useCheckboxToggle(getNodeContent, setNodeContent, saveNodeContent);
  const handleTaskInsert = useTaskInsert(getNodeContent, setNodeContent, saveNodeContent);

  // The action row: [LLM Response | model] [Read further | model]. When
  // the second group wraps onto a line of its own (a phone), the row
  // becomes a two-column grid, buttons in one column and pickers in the
  // other, so the two button/picker divides line up. Back to one line when
  // there is room again (the width both groups took side by side is kept
  // from when the wrap was seen).
  const [actionsStacked, setActionsStacked] = useState(false);
  const actionRowEl = useRef(null);
  const actionRowObserver = useRef(null);
  const actionsFlatWidth = useRef(0);
  const checkActionsStacked = useCallback(() => {
    const row = actionRowEl.current;
    if (!row) return;
    const groups = row.querySelectorAll('[data-action-group]');
    if (groups.length < 2) {
      setActionsStacked(false);
      return;
    }
    const style = window.getComputedStyle(row);
    if (style.display === 'grid') {
      if (row.clientWidth >= actionsFlatWidth.current) setActionsStacked(false);
      return;
    }
    const [first, second] = groups;
    if (second.offsetTop > first.offsetTop) {
      actionsFlatWidth.current = first.offsetWidth + second.offsetWidth
        + (parseFloat(style.columnGap) || 0);
      setActionsStacked(true);
    }
  }, []);
  const actionRowRef = useCallback((el) => {
    actionRowObserver.current?.disconnect();
    actionRowObserver.current = null;
    actionRowEl.current = el;
    if (el && typeof ResizeObserver !== 'undefined') {
      actionRowObserver.current = new ResizeObserver(() => checkActionsStacked());
      actionRowObserver.current.observe(el);
    }
  }, [checkActionsStacked]);
  // After every render: a new model label can change the widths.
  useLayoutEffect(() => { checkActionsStacked(); });

  if (loading) return <div style={{ color: "var(--text-muted)", padding: "20px" }}>Loading node...</div>;
  if (error) return <div style={{ color: "var(--accent)", padding: "20px" }}>{error}</div>;
  if (!node) return <div style={{ color: "var(--text-muted)", padding: "20px" }}>No node found.</div>;

  const handleBubbleClick = (nodeId, e) => {
    if (e && (e.metaKey || e.ctrlKey)) {
      window.open(`/node/${nodeId}`, '_blank');
    } else {
      openNode(nodeId, () => navigate(`/node/${nodeId}`));
    }
  };

  // Mirror the focal isOwner derivation (same as NodeDetail.js:216) for
  // any ancestor or child node, using the user_id / parent_user_id
  // fields added in the matching backend change.
  const ownedByMe = (n) => !!currentUser && (
    n.user_id === currentUser.id
    || (n.node_type === "llm" && n.parent_user_id === currentUser.id)
  );

  // "Glean for this reflection" (#435): in a thread NOT started from the
  // Glean card, the way to glean is the entry's menu, at any time, days
  // later too. Not on a system prompt (no reflection there) nor on a
  // reply still being written or kept away from AI.
  const gleanFromMenu = (n, owned = ownedByMe(n)) => !!currentUser?.glean_enabled
    && !node?.glean_thread && owned && !n.deleted && !n.is_system_prompt
    && n.ai_usage !== 'none'
    && !['pending', 'processing'].includes(n.llm_task_status);
  // A failed reply is never read (#435 review): a glean asked for on one,
  // from its menu or with the Glean button under it, starts from its
  // parent. The server applies the same rule.
  const gleanTargetOf = (n) => {
    if (!(n.node_type === 'llm' || n.llm_model) || n.llm_task_status !== 'failed') return n.id;
    const ancestors = node.ancestors || [];
    if (n.id === node.id) return ancestors.length ? ancestors[ancestors.length - 1].id : n.id;
    const i = ancestors.findIndex((a) => a.id === n.id);
    if (i > 0) return ancestors[i - 1].id;
    const parentIn = (kids, parentId) => {
      for (const c of kids || []) {
        if (c.id === n.id) return parentId;
        const found = parentIn(c.children, c.id);
        if (found) return found;
      }
      return null;
    };
    return parentIn(node.children, node.id) ?? n.id;
  };
  const gleanMenuItem = (n) => ({
    label: GLEAN_MENU_LABEL,
    action: () => handleReadFromNode(gleanTargetOf(n)),
    color: 'var(--text-primary)',
  });

  const buildActions = (n) => {
    // `kind` is the stable identifier — Bubble pulls the reply action
    // out of this list to wire the comment-icon click. The label is
    // free to change without breaking that coupling.
    const actions = [{
      kind: 'reply',
      label: 'Reply',
      action: () => setExclusiveTarget('reply', n),
      color: 'var(--text-primary)',
    }];
    if (ownedByMe(n)) {
      actions.push({
        label: 'Edit',
        action: () => setExclusiveTarget('edit', n),
        color: 'var(--text-primary)',
      });
      actions.push({
        label: 'Delete',
        action: () => setExclusiveTarget('delete', n),
        color: 'var(--accent)',
      });
      if (gleanFromMenu(n)) actions.push(gleanMenuItem(n));
    }
    return actions;
  };

  // For user-typed nodes: owner must match current user
  // For LLM nodes: parent node's owner must match current user (they requested the response)
  const isOwner = node.user && currentUser && (
    node.user.id === currentUser.id ||
    (node.node_type === "llm" && node.parent_user_id === currentUser.id)
  );

  const canPin = isOwner && node.privacy_level !== "private";
  const isPinned = !!node.pinned_at;

  const handlePin = async () => {
    if (pinLoading) return;
    setPinLoading(true);
    try {
      if (isPinned) {
        await api.delete(`/nodes/${id}/pin`);
        setNode({ ...node, pinned_at: null });
      } else {
        const res = await api.post(`/nodes/${id}/pin`);
        setNode({ ...node, pinned_at: res.data.pinned_at });
      }
    } catch (err) {
      console.error("Error toggling pin:", err);
      setError(err.response?.data?.error || "Error toggling pin.");
    }
    setPinLoading(false);
  };

  // The thread's root as this page knows it: the topmost ancestor the
  // viewer can see, else the node itself. The orphaned-prompt check, the
  // prompt dialog's copy and the landing once the session is gone all
  // read this one node, so the dialog's "leaves your public page" and
  // the page the delete lands on agree in a mixed-privacy thread too.
  const threadRoot = node.ancestors?.length ? node.ancestors[0] : node;

  const handleConfirmDelete = ({ withDescendants }) => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setDeleteTarget(null);
    // Would this leave the session with only its system prompt? The
    // root would stay alive and the Log would show a card whose title
    // and preview are the prompt text. Ask before that happens. Only a
    // system-prompt thread can end up there, and the root is already on
    // the page, so other threads skip the round trip. The check is
    // advisory: if it fails, the delete goes ahead as asked.
    if (!threadRoot.is_system_prompt || targetId === threadRoot.id) {
      performDelete(targetId, withDescendants, false);
      return;
    }
    api
      .get(`/nodes/${targetId}/delete-impact`, { params: { delete_descendants: withDescendants } })
      .then((r) => (r.data && r.data.orphaned_system_prompt_id) || null)
      .catch(() => null)
      .then((promptRootId) => {
        if (promptRootId) {
          setPendingPromptDelete({ targetId, withDescendants });
        } else {
          performDelete(targetId, withDescendants, false);
        }
      });
  };

  const handleConfirmPromptDelete = ({ includePrompt }) => {
    if (!pendingPromptDelete) return;
    const { targetId, withDescendants } = pendingPromptDelete;
    setPendingPromptDelete(null);
    // One request either way: the answer rides along as a flag and the
    // server re-checks under the root's lock that nothing else is left,
    // so an entry that arrived since the check (another device, the
    // Voice chain) keeps the session.
    performDelete(targetId, withDescendants, includePrompt);
  };

  const performDelete = (targetId, withDescendants, includePrompt) => {
    const wasFocal = targetId === node.id;
    api
      .delete(`/nodes/${targetId}`, {
        params: {
          delete_descendants: withDescendants,
          delete_orphaned_prompt: includePrompt,
        },
      })
      .then((response) => {
        const data = response.data || {};
        const n = data.scheduled || 1;
        addToast(
          `Deleted ${n} node${n === 1 ? "" : "s"}`,
          3000,
        );
        // Where to land when nothing of this thread is left to show:
        // public roots live in the Commons, everything else in the Log.
        // `listed` is the node whose privacy says which of the two.
        const leaveThread = (listed) => {
          if (listed.privacy_level === "public"
              && currentUser?.share_v1_enabled) {
            navigate("/commons");
          } else {
            navigate("/log");
          }
        };
        // The session root went with the target: nothing of ours is
        // alive in the thread any more (the server took the root only
        // because nothing was left), so there is no node to refetch or
        // walk up to — the page being viewed is the root, the target,
        // or something under them. The root is what was listed, and
        // what the prompt dialog's copy was written from.
        if (data.orphaned_prompt_deleted) {
          leaveThread(threadRoot);
          return undefined;
        }
        // If the cascade swept the focal node away (target is an
        // ancestor of focal AND descendants were included), refetching
        // focal would 404. Treat this like a focal-target delete and
        // walk up to the closest alive ancestor of the deleted target.
        const ancestorIdx = (!wasFocal && withDescendants && node.ancestors)
          ? node.ancestors.findIndex((a) => a.id === targetId)
          : -1;
        const focalCascaded = ancestorIdx !== -1;
        if (!wasFocal && !focalCascaded) {
          // Non-focal target: refetch the focal node so the just-deleted
          // ancestor/child surfaces as a tombstone preview in place.
          return api.get(`/nodes/${id}`).then((r) => {
            setNode(r.data);
            setQuotesVersion((v) => v + 1);
          });
        }
        // Walk up to the closest alive ancestor. For the focal-target
        // case, that's everything in node.ancestors; for the cascade
        // case, it's everything strictly above the deleted target. The
        // immediate parent may itself be a tombstone (chained "this
        // only"), and the surviving ancestor's view shows the just-
        // deleted node as a tombstoned child preview — so the tombstone
        // stays visible without dropping the user on a 404.
        const upperBound = focalCascaded ? ancestorIdx : (node.ancestors?.length ?? 0);
        if (node.ancestors) {
          for (let i = upperBound - 1; i >= 0; i -= 1) {
            if (!node.ancestors[i].deleted) {
              navigate(`/node/${node.ancestors[i].id}`);
              return;
            }
          }
        }
        // No alive ancestor (deleted a root).
        leaveThread(node);
        return undefined;
      })
      .catch((err) => {
        console.error(err);
        setError("Error deleting node.");
      });
  };

  const requestLlmFor = async (parentNodeId, { sourceMode = 'textmode' } = {}) => {
    const response = await api.post(`/nodes/${parentNodeId}/llm`, {
      model: selectedModel,
      source_mode: sourceMode,
    });
    const newNodeId = response.data.node_id;
    if (!newNodeId) throw new Error("Failed to get a task ID for the new LLM node.");
    return newNodeId;
  };

  // Shared handling for a failed LLM-request (the /nodes/:id/llm POST),
  // used by the explicit button and the auto-generate-on-submit/edit paths.
  // - 402 (monthly spend cap): surfaced globally by SpendCapBanner — return
  //   silently so the thread stays exactly where the user was.
  // - otherwise: toast. Never setError() here — that replaces the whole
  //   node view (thread, inline form and all) with the failure text and
  //   strands the user on a dead end.
  const handleLlmRequestError = (err) => {
    console.error(err);
    const status = err?.response?.status;
    const apiErr = err?.response?.data?.error;
    if (status === 402) return;
    addToast(apiErr || err.message || "Error requesting LLM response.", 8000);
  };

  // The reply is watched on its own node from the start (#367): the
  // model thinking, then the text as it is written.
  const handleLLMResponse = () => {
    setError("");
    setLlmRequesting(true);
    requestLlmFor(id)
      .then((newNodeId) => navigate(`/node/${newNodeId}?awaitLlm=${newNodeId}`,
                                    { state: FROM_PARENT }))
      .catch(handleLlmRequestError)
      .finally(() => setLlmRequesting(false));
  };

  // Gate + fire a child LLM generation under `parentNodeId`. Returns the
  // new pending LLM node id, or null if auto-generate is off or the
  // ancestry blocks AI. Re-checks context at submit time (not just on
  // mount): if ANY node in `chainNodes` has `ai_usage` outside {chat,
  // train}, auto-generate silently skips and toasts the user. Prevents
  // firing an LLM call that would omit parts of the thread from context
  // and produce partial / confusing replies.
  const tryAutoGenerateFor = async (parentNodeId, chainNodes) => {
    if (!autoGenerateActive) return null;
    if (!contextAllowsAi(chainNodes)) {
      setAutoGenerate(false);
      addToast(
        'Turning off auto-generate. AI usage on some nodes is turned off.',
        8000,
      );
      return null;
    }
    return await requestLlmFor(parentNodeId);
  };

  // Called by NodeForm after it successfully POSTs /nodes/.
  // `data` is the newly-created child node from the backend.
  const handleInlineSuccess = async (data) => {
    const newNodeId = data?.id;
    if (!newNodeId) return;
    setError("");
    try {
      const chain = [node, ...(node.ancestors || [])];
      const llmNodeId = await tryAutoGenerateFor(newNodeId, chain);
      if (llmNodeId) {
        // Through the NEW USER node so it gets its own URL/history step
        // (a back press then walks the actual entries); ?awaitLlm takes
        // the view on from there to the pending response.
        navigate(`/node/${newNodeId}?awaitLlm=${llmNodeId}`);
      } else {
        navigate(`/node/${newNodeId}`);
      }
    } catch (err) {
      // The user's node WAS created; only the LLM request failed. Always
      // land on the new node so it shows, gets its history entry, and the
      // input resets — then surface the failure (402's banner fires
      // globally; the rest toast via handleLlmRequestError).
      navigate(`/node/${newNodeId}`);
      handleLlmRequestError(err);
    }
  };

  // A reply typed or recorded under a read reply is a Text-mode message
  // (#323): the backend attaches the textmode prompt under the read reply
  // when no agentic prompt sits above it, so the conversation about the
  // picks runs with the assistant's tools and what the user says there
  // can move their intentions and memory. The read itself never runs
  // under that prompt (the task strips it on read turns). Auto-generate
  // is honoured like Text mode's own entry, and the reply fires
  // server-side, so there is no /nodes/<id>/llm follow-up here.
  // The form's AI usage goes along (a recording carries it on its draft):
  // it defaults to the thread's, not the read reply's 'chat' (#362).
  const submitReadReplyMessage = async ({ content, privacy_level, ai_usage, streaming_session_id }) => {
    if (streaming_session_id) {
      const res = await api.post(
        `/drafts/streaming/${streaming_session_id}/save-as-node`,
        { content, agentic: true, auto_generate: autoGenerateActive, model: selectedModel },
      );
      return res.data;
    }
    if (ai_usage === 'none') {
      // Kept away from AI: a plain note, no Text-mode prompt and no reply
      // (Text mode requires 'chat' or 'train').
      const res = await api.post('/nodes/', {
        content, parent_id: parseInt(id, 10), privacy_level, ai_usage,
      });
      return res.data;
    }
    const res = await api.post(`/textmode/from-node/${id}`, {
      content, ai_usage, model: selectedModel, auto_generate: autoGenerateActive,
    });
    return res.data;
  };
  const handleReadReplySuccess = (data) => {
    const userNodeId = data?.user_node_id || data?.id;
    if (!userNodeId) return;
    const suffix = data?.llm_node_id ? `?awaitLlm=${data.llm_node_id}` : '';
    navigate(`/node/${userNodeId}${suffix}`);
  };

  // Called after NodeForm successfully PUTs /nodes/<id>. Updates local
  // state, closes the overlay, and — if the edited node is user-authored
  // — fires a fresh LLM child off it. Re-running on a node that already
  // has an LLM child produces a new sibling, i.e. a new branch off the
  // edit. Editing an LLM node never triggers generation: nothing for it
  // to reply to.
  const handleEditSuccess = async (data) => {
    const wasFocal = editTarget?.id === node.id;
    setShowEditOverlay(false);
    setEditTarget(null);

    // Privacy / AI usage cascaded to the user's replies as well.
    const cascaded = data?.descendants_updated || 0;
    if (cascaded) {
      addToast(
        `Applied to ${cascaded} repl${cascaded === 1 ? "y" : "ies"} too`,
        3000,
      );
    }

    if (!wasFocal) {
      // Non-focal edit: refetch the focal node so the edited
      // ancestor/child reflects new content. No auto-LLM trigger —
      // the user is in a different conversation context.
      try {
        const refreshed = await api.get(`/nodes/${id}`).then((r) => r.data);
        setNode(refreshed);
        setQuotesVersion((v) => v + 1);
      } catch (err) {
        console.error(err);
      }
      return;
    }

    // PUT returns the node's own fields only (no ancestors/children —
    // re-serializing the thread on every save was the slow part); the
    // tree we already hold is unchanged by an edit, so merge.
    let updated = data.node ? { ...node, ...data.node } : { ...node, content: data.content };
    if (cascaded) {
      // The replies' settings changed too — the tree we hold is stale.
      try {
        updated = await api.get(`/nodes/${id}`).then((r) => r.data);
      } catch (err) {
        console.error(err);
      }
    }
    setNode(updated);
    const editedIsLlm = updated.node_type === "llm" || !!updated.llm_model;
    if (editedIsLlm) return;
    try {
      const chain = [updated, ...(updated.ancestors || [])];
      const llmNodeId = await tryAutoGenerateFor(updated.id, chain);
      // The edited node keeps its history entry; the response is watched
      // on its own node from the start (#367).
      if (llmNodeId) {
        navigate(`/node/${llmNodeId}?awaitLlm=${llmNodeId}`,
                 { state: FROM_PARENT });
      }
    } catch (err) {
      handleLlmRequestError(err);
    }
  };

  const handleSessionFromNode = (sessionType) => {
    setVoiceLoading(true);
    setError("");
    api
      .post(`/${sessionType}/from-node/${id}`, { model: selectedModel })
      .then((response) => {
        const { mode, llm_node_id, parent_id, fresh } = response.data;
        // A thread started from the Glean card keeps its Glean button
        // on the Voice screen too (#435).
        const glean = (sessionType === 'voice' && node?.glean_thread
          && currentUser?.glean_enabled) ? '&glean=1' : '';
        if (mode === "processing") {
          let url = `/voice?resume=${llm_node_id}`;
          if (parent_id) url += `&parent=${parent_id}`;
          if (fresh) url += `&fresh=1`;
          navigate(url + glean);
        } else {
          navigate(`/voice?parent=${parent_id}${glean}`);
        }
      })
      .catch((err) => {
        console.error(err);
        setVoiceLoading(false);
        // 402 = spend cap, surfaced globally by SpendCapBanner; don't echo
        // the raw error code into the view.
        if (err?.response?.status === 402) return;
        // AI usage keeps this thread away from AI: the Voice screen says
        // so, and where to change it, in place of the record button.
        if (sessionType === 'voice' && isAiUsageRefusedError(err)) {
          navigate(`/voice?parent=${id}`);
          return;
        }
        // 410: this node was deleted after the page opened (#480). A toast,
        // as for the LLM Response button: setError() would replace the
        // whole thread with the message and leave the user nowhere to go.
        if (err?.response?.status === 410) {
          addToast(err.response.data?.error || 'This node has been deleted.', 8000);
          return;
        }
        setError(err.response?.data?.error || `Error starting ${sessionType} session.`);
      });
  };

  // Glean (#435): the 'read_thread' prompt is attached under the node by
  // reference and the reply under it, a live call; we land on the pending
  // gleaning, which this page polls until the picks arrive. Inside a
  // thread that has gleaned already the same call gleans again (no
  // prompt, a read turn under the node). The click is the request, so
  // auto-generate does not apply. *targetId* is the node whose menu
  // asked ("Glean for this reflection"), else this page's node.
  const handleReadFromNode = (targetId = id) => {
    setReadLoading(true);
    setError("");
    api
      .post(`/read/from-node/${targetId}`, {
        // The model picked beside the Glean button (everyone who gleans
        // has the picker, #435); without one the server takes the
        // default, the glean model set for the user's provider.
        model: readModel || undefined,
      })
      .then((response) => {
        const { llm_node_id, prompt_node_id } = response.data;
        navigate(`/node/${llm_node_id || prompt_node_id}`);
      })
      .catch((err) => {
        setReadLoading(false);
        if (err?.response?.status === 402) return;
        const msg = err.response?.data?.error || 'Could not start the glean.';
        addToast(msg, 6000);
      });
  };

  // Ancestors section rendered as a list of bubbles.
  const ancestorsSection = node.ancestors && node.ancestors.length > 0 && (
    <div style={{ display: "flex", flexDirection: "column", marginBottom: "10px" }}>
      {node.ancestors.map((ancestor) => (
        <Bubble
          key={ancestor.id}
          node={ancestor}
          onClick={handleBubbleClick}
          leftAlign={true}
          actions={buildActions(ancestor)}
        />
      ))}
    </div>
  );

  // Highlighted node section
  const highlightedTextStyle = {
    boxSizing: "border-box",
    padding: "1.8rem 2rem",
    margin: "18px 0 10px 0",
    backgroundColor: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderLeft: "3px solid var(--accent)",
    borderRadius: "10px",
    // Reserve right-side space for the always-outside kebab so the
    // visible gap to the right of the icon matches the gap between
    // bubble and icon (~14px). Drops the 95% scaling — maxWidth still
    // caps the bubble on wide viewports.
    width: "calc(100% - 50px)",
    maxWidth: "1500px",
    marginLeft: "20px",
    position: "relative",
  };

  const actionContainerStyle = {
    marginLeft: "20px",
    marginBottom: "10px"
  };

  // Compute children count for the highlighted node.
  const highlightedChildrenCount = typeof node.child_count !== "undefined"
    ? node.child_count
    : (node.children ? node.children.length : 0);

  // Determine humanOwnerUsername for LLM nodes in NodeDetail
  // node.parent_user_id is the human owner's user_id for LLM nodes
  const humanOwnerUsername = node.node_type === "llm" && node.parent_user_id
    ? (node.ancestors && node.ancestors.length > 0
      ? (() => {
          // Walk ancestors to find the human owner username
          for (let i = node.ancestors.length - 1; i >= 0; i--) {
            const a = node.ancestors[i];
            if (a.node_type !== "llm") return a.username;
          }
          return null;
        })()
      : null)
    : null;

  const isLlmNode = node.node_type === "llm" || !!node.llm_model;
  const isLlmPending = isLlmNode && (
    node.llm_task_status === 'pending'
    || node.llm_task_status === 'processing'
  );
  // A read reply: it carries (or carried) a batch, a glean's marker, or
  // hangs under one of the read prompts.
  const parentAncestor = node.ancestors?.[node.ancestors.length - 1];
  const isReadReply = isLlmNode && (
    !!node.read_reply
    || (Array.isArray(node.tool_calls_meta)
      && node.tool_calls_meta.some(tc => ['_batch', '_read', '_live'].includes(tc?.name)))
    || ['read', 'read_thread'].includes(parentAncestor?.prompt_key)
  );
  // A read reply's picks are its {quote_ext:ID} markers. Their read state
  // and good / bad verdicts live in externalQuotes, kept in step by the
  // bubbles' own controls and the tail's "Mark all as read", so the
  // tail's "n unread" is always the list as shown.
  const pickIds = isReadReply
    ? Array.from(new Set(((node.content || '').match(/\{quote_ext:(\d+)\}/g) || [])
        .map(m => m.match(/\d+/)[0])))
    : [];
  const picksLoaded = pickIds.every(pid => pid in externalQuotes);
  const picksUnread = pickIds.filter(pid => externalQuotes[pid] && !externalQuotes[pid].read_at).length;
  const handleExternalReadChange = (itemId, readAt) => setExternalQuotes(prev => (
    prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], read_at: readAt } } : prev
  ));
  const handleExternalFeedbackChange = (itemId, feedback) => setExternalQuotes(prev => (
    prev[itemId]
      ? { ...prev, [itemId]: { ...prev[itemId], feedback, feedback_shared: false } }
      : prev
  ));
  // A gleaning pick's other verdict, on Loore's intro to it (#502).
  const handleExternalIntroVerdictChange = (itemId, verdict) => setExternalQuotes(prev => (
    prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], intro_verdict: verdict } } : prev
  ));
  const handlePicksMarkedAll = (readAt) => setExternalQuotes(prev => {
    const next = { ...prev };
    Object.entries(readAt).forEach(([itemId, ts]) => {
      if (next[itemId]) next[itemId] = { ...next[itemId], read_at: ts };
    });
    return next;
  });
  // Inside a read thread the Glean button gleans again: /read/from-node
  // makes a read turn under this node with the whole thread in view, no
  // second prompt. The backend says which it is (in_read_thread: a read
  // prompt above, by key or by the {ca_tweets} placeholder in a PoC-era
  // prompt's text), the same test the route applies; a read reply is one
  // by definition.
  const inReadThread = !!node.in_read_thread || isReadReply;
  // Has the thread gleaned already? The button's label stays "Glean"; its
  // tooltip says what the next press does.
  const readReplyAbove = !!node.read_reply_above || isReadReply;
  const readLabel = 'Glean';
  const readTitle = readReplyAbove ? GLEAN_AGAIN_TITLE : GLEAN_TITLE;
  // Who sees Glean at all (#435): the rollout gate and the user's own
  // switch, both in glean_enabled. A thread started from the Glean card
  // offers the Glean button; any other thread "Glean for this
  // reflection" in the entry's menu.
  const gleanEnabled = !!currentUser?.glean_enabled;
  const gleanThread = !!node.glean_thread;
  // The gleaning's own head (#435): "Today's gleanings", how many picks,
  // or the empty day. Old replies that kept their picks in rows only
  // (FeedPicks below) are not empty.
  const gleaningDone = isReadReply && node.llm_task_status === 'completed';
  const gleaningEmpty = gleaningDone && !!node.read_window
    && pickIds.length === 0 && !(node.feed_picks_count > 0);
  const tweetsRead = Number(node.read_window?.tweets || 0);
  const gleaningFailed = isReadReply && node.llm_task_status === 'failed';
  const showProposal = !!node.content && !isLlmPending && (
    (isLlmNode && hasProposalSections(node.content))
    // User-authored nodes: the owner can write/paste fenced :::share
    // blocks directly (e.g. after splitting one proposed post into two
    // nodes) — same card + Save flow as LLM proposals. Fence syntax only
    // (legacy ### Share headings stay LLM-only), and gated on the user's
    // sharing opt-in so no Save button appears that would 404.
    || (!isLlmNode && isOwner && !!currentUser?.share_v1_enabled
        && hasShareBlocks(node.content))
  );
  // When a proposal is present, the lead-in renders above the card and any
  // trailing commentary below it (proposalAfter). Otherwise show full content.
  const proposalShareOnly = showProposal && !isLlmNode;
  const proposalSplit = showProposal
    ? splitProposalText(node.content, { shareOnly: proposalShareOnly })
    : null;
  const displayContent = showProposal ? proposalSplit.before : node.content;
  const proposalAfter = showProposal ? proposalSplit.after : '';
  // Inline input is always available to any viewer (reply + branch from
  // someone else's public node → new thread owned by the replier). The
  // only way to add a child text node since "Add Text" was removed. On
  // `ai_usage='none'` nodes the submit path skips the LLM request.
  // Ownership gates apply elsewhere: Voice Mode (backend 403s non-owners
  // via /voice/from-node), Craft-bar LLM Response + ModelSelector, and
  // the kebab Edit/Delete menu.
  const showInlineInput = !!currentUser;
  // Auto-generate hides LLM Response because it already asked for the
  // reply when the node was sent. In a read thread it doesn't always: an
  // audio upload under the picks is saved without a reply, because
  // auto-generate can't know whether the user wants to talk about it
  // (LLM Response) or get more picks (Read further). When Loore can't
  // know what the user wants next, it shows the choices (#387): a node
  // of the user's in a read thread with no reply under it yet offers
  // both, auto-generate on or off.
  const awaitsChoiceInRead = inReadThread && !isLlmNode
    && !(node.children || []).some(c => !c.deleted);
  const showCraftBar = isOwner && (craftMode || isPublicThread)
    && (!autoGenerateActive || awaitsChoiceInRead)
    && node.ai_usage !== 'none' && !isLlmPending;
  // In a thread started from the Glean card (#435) the action row under
  // every node carries the Glean button, next to LLM Response — the
  // conversation can get long and nothing is pinned to the viewport
  // (small screens), so the action travels with the node the user is on.
  // It shows whenever the owner could act, not only in craft mode; LLM
  // Response keeps the craft-bar rule. Any other thread has no Glean
  // button: "Glean for this reflection" is in the entry's menu. Beside
  // the button sits the model picker, for everyone who gleans (Peter,
  // 2026-10-09: the same read models as admins, other providers'
  // included); its default is the glean model set for the user's provider
  // (/suggested-model?purpose=read). Directly under a finished gleaning LLM
  // Response is disabled: a reply asked for there is another glean (the
  // task's parent rule), and the way to talk about the picks is a
  // comment first, whose own row then offers LLM Response again.
  const underReadReply = isReadReply && node.llm_task_status === 'completed';
  // The Glean entry (the read prompt a glean attaches, shown as "Glean")
  // carries Glean and its picker too (Peter, 2026-10-09), in any thread:
  // it is Glean's own entry, and after a failed gleaning it is where the
  // page lands, so it is where the user tries again. Any other system
  // prompt has no reflection to glean from.
  const isGleanEntry = !!node.is_system_prompt
    && ['read', 'read_thread'].includes(node.prompt_key);
  const readActions = isOwner && gleanEnabled
    && (gleanThread || isGleanEntry)
    && node.ai_usage !== 'none' && !isLlmPending
    && (!node.is_system_prompt || isGleanEntry);
  // Before the first picks (the read prompt itself, or a note typed
  // under it) a reply asked for here would be that first read, so the
  // generic LLM Response is not offered at all: the row is "Read" and
  // its model picker.
  const showLlmResponse = showCraftBar && !(inReadThread && !readReplyAbove);
  // Each action carries its own model picker, joined to its right edge:
  // [LLM Response | Opus 4.6 v]  [Read further | GPT-6 Luna v] (#355).
  // Stacked (see actionsStacked), a group lends its button and picker to
  // the row's grid; each then fills its column, label centred, chevron at
  // the right edge.
  const actionGroupStyle = actionsStacked
    ? { display: 'contents' }
    : { display: 'inline-flex', alignItems: 'stretch' };
  const joinedButtonStyle = {
    borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none',
    justifyContent: 'center',
  };
  const joinedPickerStyle = {
    borderTopLeftRadius: 0, borderBottomLeftRadius: 0,
    flex: 1, justifyContent: 'space-between',
  };
  const readBusy = readLoading || llmRequesting || !!llmTaskNodeId || moving;
  const readButton = (
    <span data-action-group style={actionGroupStyle}>
      <LooreTooltip text={readTitle}>
        <button
          // Under a failed reply the next one starts where it did (its
          // parent), so the failed reply is not part of what is read.
          onClick={() => handleReadFromNode(gleanTargetOf(node))}
          disabled={readBusy}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', ...joinedButtonStyle }}
        >
          {readLoading ? 'Starting…' : readLabel}
        </button>
      </LooreTooltip>
      <ModelSelector
        nodeId={node.id}
        purpose="read"
        selectedModel={readModel}
        onModelChange={setReadModel}
        disabled={readBusy}
        style={joinedPickerStyle}
      />
    </span>
  );
  const llmResponseTitle = underReadReply
    ? `To chat about the gleaning, send your reply first.${readActions ? ' To glean again, use Glean.' : ''}`
    : (inReadThread ? "Chat about the picks" : undefined);

  // Shared shell for the top-right controls. Voice Mode + Auto-generate
  // share padding/border/typography; Auto-generate uses `space-between`
  // (label left, pill right) to match the Craft-mode toggle in the
  // NavBar overflow menu. They sit side-by-side in a single row so the
  // fixed strip stays above the Thread / ancestor hr separator.
  const topRightButtonStyle = {
    background: 'none',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    padding: '0 12px',
    color: 'var(--text-muted)',
    fontFamily: 'var(--sans)',
    fontSize: '0.78rem',
    fontWeight: 300,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    width: '172px',
    height: '32px',
    boxSizing: 'border-box',
  };


  // Top-right controls (Voice Mode + Auto-generate). Rendered in the
  // same flex row as the Thread heading so they align vertically and
  // scroll away with content (no absolute positioning / viewport
  // anchoring). Voice Mode shows on a node AI may not read too: the
  // Voice screen explains there instead of recording. Auto-generate
  // needs AI and stays hidden on it. In a Glean-card thread Glean is in the
  // action row; in any other thread it is here and in the entry's menu.
  // Glean in a Reflect thread (voice review, Peter, 2026-10-13): a Glean
  // button next to Voice Mode that does what "Glean for this reflection"
  // does on the user's own newest entry of the thread (same route, same
  // refusals, same failed-gleaning display). Hidden when the thread has
  // no entry of theirs yet. The newest is by created_at; entries without
  // one keep thread order (ancestors, this node, then its descendants).
  const newestOwnEntry = (() => {
    if (!currentUser?.glean_enabled || node.glean_thread) return null;
    const flat = [];
    const walk = (kids) => (kids || []).forEach((c) => { flat.push(c); walk(c.children); });
    walk(node.ancestors);
    flat.push(node);
    walk(node.children);
    let best = null;
    flat.forEach((n) => {
      // The focal node has `user: {id}` and no user_id; the rest have user_id.
      if (n.node_type === 'llm' || (n.user_id ?? n.user?.id) !== currentUser.id) return;
      if (!gleanFromMenu(n, true)) return;
      if (!best || !n.created_at || !best.created_at || n.created_at >= best.created_at) best = n;
    });
    return best;
  })();
  // Beside the Glean button sits the model picker (Peter, 2026-10-09), the
  // same control and state as in a Glean-card thread; the group is the
  // other top-right buttons' 172px wide, so it lines up with them, and
  // in their font size. 66px holds "Starting…"; the picker's 106px holds
  // "GPT-5.6 Luna", the default glean model, and its arrow (a longer
  // name ends in "…").
  const topGleanBusy = readLoading || llmRequesting || !!llmTaskNodeId || moving;
  // Not on the Glean entry itself: its action row already has Glean and its picker.
  const topGleanButton = newestOwnEntry && !readActions && (
    <span style={{ display: 'inline-flex', alignItems: 'stretch', width: '172px', height: '32px' }}>
      <LooreTooltip text={GLEAN_TITLE}>
        <button
          type="button"
          onClick={() => handleReadFromNode(gleanTargetOf(newestOwnEntry))}
          disabled={topGleanBusy}
          style={{
            ...topRightButtonStyle, ...joinedButtonStyle, width: '66px', padding: '0 6px',
          }}
        >
          {readLoading ? 'Starting…' : 'Glean'}
        </button>
      </LooreTooltip>
      <ModelSelector
        nodeId={newestOwnEntry.id}
        purpose="read"
        selectedModel={readModel}
        onModelChange={setReadModel}
        disabled={topGleanBusy}
        style={{
          ...joinedPickerStyle, width: '106px', height: '32px', boxSizing: 'border-box',
          padding: '0 8px', gap: '4px', fontSize: topRightButtonStyle.fontSize,
        }}
      />
    </span>
  );
  const nodeAllowsAi = node.ai_usage !== 'none';
  const topRightControls = isOwner && !isPublicThread && (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '6px',
    }}>
      <button
        onClick={() => handleSessionFromNode('voice')}
        disabled={voiceLoading}
        style={{ ...topRightButtonStyle, justifyContent: 'space-between' }}
        title="Continue this conversation by voice"
      >
        <span>{voiceLoading ? 'Starting…' : 'Voice Mode'}</span>
        <span style={{
          width: '32px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 0,
        }}>
          <FaMicrophone size={12} />
        </span>
      </button>
      {topGleanButton}
      {craftMode && nodeAllowsAi && (
        <button
          type="button"
          onClick={() => setAutoGenerate(v => !v)}
          title={autoGenerate ? 'Auto-generate is on — click to turn off' : 'Auto-generate is off — click to turn on'}
          style={{ ...topRightButtonStyle, justifyContent: 'space-between' }}
        >
          <span>Auto-generate</span>
          <span style={{
            width: '32px',
            height: '18px',
            borderRadius: '9px',
            background: autoGenerate ? 'var(--accent)' : 'var(--border)',
            position: 'relative',
            transition: 'background 0.2s ease',
            flexShrink: 0,
          }}>
            <span style={{
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              background: 'var(--text-primary)',
              position: 'absolute',
              top: '2px',
              left: autoGenerate ? '16px' : '2px',
              transition: 'left 0.2s ease',
            }} />
          </span>
        </button>
      )}
    </div>
  );

  const highlightedNodeSection = (
    <div ref={highlightedNodeRef} style={{ position: 'relative' }}>
      <hr style={{ borderColor: "var(--border)", margin: 0 }} />
      <div style={highlightedTextStyle}>
        {isOwner && (
          <BubbleKebabMenu
            visible={true}
            items={[
              {
                label: 'Edit',
                action: () => setExclusiveTarget('edit', node),
                color: 'var(--text-primary)',
              },
              {
                label: 'Delete',
                action: () => setExclusiveTarget('delete', node),
                color: 'var(--accent)',
              },
              ...(gleanFromMenu(node, isOwner) && !readLoading && !moving
                ? [gleanMenuItem(node)] : []),
            ]}
          />
        )}
        {node.is_system_prompt && node.prompt_title && (
          <div style={{
            fontFamily: "var(--sans)",
            fontSize: "0.8rem",
            fontWeight: 300,
            color: "var(--text-muted)",
            marginBottom: "0.6rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
          }}>
            <span>{node.prompt_title}{node.prompt_version_number ? ` v${node.prompt_version_number}` : ''}</span>
            {node.context_artifacts?.profile && (
              <span style={{ opacity: 0.7 }}>
                {'\u00B7'} Profile v{node.context_artifacts.profile.version_number}
              </span>
            )}
            {node.context_artifacts?.todo && (
              <span style={{ opacity: 0.7 }}>
                {'\u00B7'} TODO v{node.context_artifacts.todo.version_number}
              </span>
            )}
          </div>
        )}
        {isReadReply && (
          <div className="gleanings-head">
            <h3 className="gleanings-title">{GLEANINGS_TITLE}</h3>
            {gleaningDone && pickIds.length > 0 && (
              <p className="gleanings-sub">
                {`${pickIds.length} ${pickIds.length === 1 ? 'tweet' : 'tweets'} from today, chosen for what you said.`}
              </p>
            )}
          </div>
        )}
        {gleaningEmpty && (
          <div className="gleanings-empty">
            <p className="gleanings-empty-big">Nothing worth your time today.</p>
            <p className="gleanings-empty-small">
              {tweetsRead > 0
                ? `Loore read all ${tweetsRead.toLocaleString('en-US')} of today's tweets in the archive.`
                : 'The archive had no new tweets today.'}
            </p>
          </div>
        )}
        {isReadReply && node.read_window && (
          <ReadWindowLine window={node.read_window} />
        )}
        {isLlmPending && partialReplyText(streamText).trim() ? (
          // #367: the reply as far as it's written, then the same pulsing
          // dots — the text is still coming.
          <div>
            <QuotedContent
              content={partialReplyText(streamText)}
              quotes={{}}
              externalQuotes={{}}
              nodeId={node.id}
              contextArtifacts={node.context_artifacts || null}
              onQuoteClick={handleBubbleClick}
            />
            <span aria-label="Still writing" style={{
              display: 'inline-flex', gap: '3px', padding: '6px 0',
            }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  width: '5px', height: '5px', borderRadius: '50%',
                  background: 'var(--text-muted)',
                  animation: `wopPulseDot 1.2s ease-in-out ${i * 0.15}s infinite`,
                }} />
              ))}
            </span>
            <style>{`
              @keyframes wopPulseDot {
                0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
                30% { opacity: 1; transform: translateY(-2px); }
              }
            `}</style>
          </div>
        ) : isLlmPending ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            color: 'var(--text-muted)',
            fontFamily: 'var(--sans)', fontSize: '0.95rem', fontWeight: 300,
            fontStyle: 'italic',
            padding: '8px 0',
          }}>
            <span>{batchMeta ? 'Processing' : (isReadReply ? 'Gleaning' : 'Thinking')}</span>
            <span style={{ display: 'inline-flex', gap: '3px' }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  width: '5px', height: '5px', borderRadius: '50%',
                  background: 'var(--text-muted)',
                  animation: `wopPulseDot 1.2s ease-in-out ${i * 0.15}s infinite`,
                }} />
              ))}
            </span>
            <style>{`
              @keyframes wopPulseDot {
                0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
                30% { opacity: 1; transform: translateY(-2px); }
              }
            `}</style>
          </div>
        ) : gleaningFailed ? (
          // A failed gleaning keeps the placeholder text as its content;
          // say what happened instead, and where to try again (#435).
          <div className="gleaning-failed" role="status">
            <p className="gleaning-failed-big">This gleaning didn't come through.</p>
            {node.llm_task_error && (
              <p className="gleaning-failed-small">{node.llm_task_error}</p>
            )}
            {isOwner && gleanEnabled && (
              <p className="gleaning-failed-small">
                {readActions
                  ? 'Press Glean below to try again.'
                  : `To try again, choose “${GLEAN_MENU_LABEL}” in your entry's ⋯ menu.`}
              </p>
            )}
          </div>
        ) : (
          (!showProposal || displayContent) && (
            <div className={isReadReply ? 'read-reply-body' : undefined}>
              <QuotedContent
                content={displayContent}
                quotes={quotes}
                externalQuotes={externalQuotes}
                nodeId={node.id}
                onExternalReadChange={handleExternalReadChange}
                onExternalFeedbackChange={handleExternalFeedbackChange}
                onExternalIntroVerdictChange={handleExternalIntroVerdictChange}
                showRecommendationFeedback={isLlmNode}
                referenceBackLabel={isReadReply ? GLEANINGS_TITLE : undefined}
                contextArtifacts={node.context_artifacts || null}
                onQuoteClick={handleBubbleClick}
                onCheckboxToggle={isOwner ? handleCheckboxToggle : undefined}
                onAddTask={isOwner ? handleTaskInsert : undefined}
              />
            </div>
          )
        )}
        {showProposal && (
          <ProposalInline
            content={node.content}
            nodeId={node.id}
            toolCallsMeta={node.tool_calls_meta}
            shareOnly={proposalShareOnly}
            canAct={!!isOwner}
            onContentChange={isOwner
              ? (newContent) => setNode(prev => prev ? { ...prev, content: newContent } : prev)
              : undefined}
            onApplied={(toolName, updates) => setNode(prev => {
              if (!prev || !Array.isArray(prev.tool_calls_meta)) return prev;
              return {
                ...prev,
                tool_calls_meta: prev.tool_calls_meta.map(tc =>
                  tc.name === toolName ? { ...tc, ...updates } : tc),
              };
            })}
            onError={(msg) => addToast(msg)}
          />
        )}
        {showProposal && proposalAfter && (
          <div style={{ marginTop: '20px' }}>
            <QuotedContent
              content={proposalAfter}
              quotes={quotes}
              externalQuotes={externalQuotes}
              nodeId={node.id}
              onExternalReadChange={handleExternalReadChange}
              onExternalFeedbackChange={handleExternalFeedbackChange}
              onExternalIntroVerdictChange={handleExternalIntroVerdictChange}
              showRecommendationFeedback={isLlmNode}
              referenceBackLabel={isReadReply ? GLEANINGS_TITLE : undefined}
              contextArtifacts={node.context_artifacts || null}
              onQuoteClick={handleBubbleClick}
            />
          </div>
        )}
        {isReadReply && node.llm_task_status === 'cancelled' && (
          // The server sends the reason to the reply's owner only. A
          // failed gleaning says its reason in its own block above
          // (gleaningFailed); a cancelled read keeps its text.
          <ReadCancelledLine reason={node.llm_task_error} />
        )}
        {/* Replies from before 2026-09-16 kept their picks in rows only;
            since then the reply text quotes each pick ({quote_ext:ID}),
            so the list is rendered by QuotedContent above. */}
        {!isLlmPending && isOwner && node.feed_picks_count > 0
          && !/\{quote_ext:\d+\}/.test(node.content || '') && (
          <FeedPicks nodeId={node.id} />
        )}
        {!isLlmPending && isOwner && isReadReply
          && node.llm_task_status === 'completed' && (
          <ReadReplyTail
            nodeId={node.id}
            unread={picksUnread}
            total={pickIds.length}
            loaded={picksLoaded}
            onMarkedAll={handlePicksMarkedAll}
          />
        )}
        <ToolActionsTaken
          toolCallsMeta={node.tool_calls_meta}
          detailed={!!isOwner}
          expanded={toolActionsExpanded}
          onToggle={() => setToolActionsExpanded(!toolActionsExpanded)}
        />
      </div>
      <div style={actionContainerStyle}>
        <NodeFooter
          username={node.user.username}
          createdAt={node.created_at}
          childrenCount={highlightedChildrenCount}
          humanOwnerUsername={humanOwnerUsername}
          llmModel={node.llm_model}
          origin={node.origin}
          publicPage={node.privacy_level === 'public'}
        >
          <button
            onClick={canPin ? handlePin : undefined}
            disabled={!canPin || pinLoading}
            title={
              !isOwner ? "Only the owner can pin"
              : node.privacy_level === "private" ? "Cannot pin a private node"
              : isPinned ? "Unpin from your public page"
              : "Pin to the top of your public page"
            }
            style={{
              background: "none",
              border: "none",
              cursor: canPin ? "pointer" : "not-allowed",
              padding: 0,
              opacity: canPin ? 1 : 0.35,
              color: isPinned ? "var(--accent)" : "inherit",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FaThumbtack />
          </button>
          <SpeakerIcon nodeId={node.id} content={node.content} isPublic={node.privacy_level === 'public'} aiUsage={node.ai_usage} onTtsGenerated={() => setNode(prev => prev ? { ...prev, has_tts: true } : prev)} />
          <DownloadAudioIcon nodeId={node.id} isPublic={node.privacy_level === 'public'} aiUsage={node.ai_usage} />
        </NodeFooter>
        {(showCraftBar || readActions) && (
          <div
            ref={actionRowRef}
            style={actionsStacked
              ? {
                marginTop: "8px", display: 'grid', rowGap: '8px',
                gridTemplateColumns: 'max-content max-content', justifyContent: 'start',
              }
              : { marginTop: "8px", display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
          >
            {showLlmResponse && (
              /* The group span carries the tooltip, for the button and its
                 model picker alike (they are one control, disabled
                 together): a disabled button or select gets no hover
                 events in some browsers. */
              <span data-action-group title={llmResponseTitle} style={actionGroupStyle}>
                <button
                  onClick={handleLLMResponse}
                  disabled={llmRequesting || !!llmTaskNodeId || underReadReply || moving}
                  aria-disabled={underReadReply || undefined}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', ...joinedButtonStyle }}
                >
                  {(llmRequesting || llmTaskNodeId) ? (
                    <>
                      <FaSpinner className="spin" aria-hidden="true" />
                      {llmRequesting ? 'Requesting…'
                        : llmStatus === 'pending' ? 'Waiting for AI…' : 'Generating…'}
                    </>
                  ) : 'LLM Response'}
                </button>
                <ModelSelector
                  nodeId={node.id}
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  disabled={llmRequesting || !!llmTaskNodeId || underReadReply || moving}
                  style={joinedPickerStyle}
                />
              </span>
            )}
            {/* Before the first picks "Read" stands alone, where LLM
                Response usually is: the response action users know, under
                its own name. After them "Read further" sits to the right
                of the (disabled or live) LLM Response. */}
            {readActions && readButton}
          </div>
        )}
        {llmTaskNodeId && !showLlmResponse && !isLlmPending && (
          <div style={{
            marginTop: '8px',
            display: 'flex', alignItems: 'center', gap: '8px',
            fontFamily: 'var(--sans)', fontSize: '0.78rem', fontWeight: 300,
            color: 'var(--text-muted)',
          }}>
            <FaSpinner className="spin" aria-hidden="true" />
            {llmStatus === 'pending' ? 'Waiting for AI…' : 'Generating…'}
          </div>
        )}
      </div>
      {showInlineInput && (
        <div style={{
          width: '95%',
          maxWidth: '1500px',
          marginLeft: '20px',
          marginRight: 'auto',
          marginTop: '4px',
          marginBottom: '12px',
        }}>
          <NodeForm
            key={`inline-${id}`}
            parentId={parseInt(id, 10)}
            hidePowerFeatures={!craftMode}
            hideAudioUpload={!craftMode}
            compact
            placeholder={isReadReply
              ? "Ask about these picks, or say what you make of them…"
              : "Type what's on your mind…"}
            onSubmitOverride={isReadReply ? submitReadReplyMessage : undefined}
            onSuccess={isReadReply ? handleReadReplySuccess : handleInlineSuccess}
          />
        </div>
      )}
      <hr style={{ borderColor: "var(--border)" }} />
    </div>
  );

  // Render the child nodes recursively.
  const childrenSection = (
    <div>
      {node.children && node.children.length > 0 && (
        <RenderChildTree
          nodes={node.children}
          onBubbleClick={handleBubbleClick}
          buildActions={buildActions}
        />
      )}
    </div>
  );

  return (
    <div style={{ padding: "8px 12px 12px" }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        // The controls column is three rows tall; the heading sits at
        // the row's foot, on the rule below, rather than floating at
        // the column's middle.
        alignItems: "flex-end",
        gap: "16px",
        marginBottom: "12px",
      }}>
        <h2 style={{
          fontFamily: "var(--serif)",
          fontWeight: 300,
          fontSize: "1.8rem",
          color: "var(--text-primary)",
          margin: 0,
        }}>Thread</h2>
        {topRightControls}
      </div>
      <SemanticNeighbors nodeId={node.id} openNode={openNode} />
      {ancestorsSection}
      {highlightedNodeSection}
      {childrenSection}

      {showPromptEditConfirm && (
        <div
          onClick={() => { setShowPromptEditConfirm(false); setEditTarget(null); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: '12px', padding: '2rem', maxWidth: '440px', width: '90vw',
            }}
          >
            <h3 style={{
              fontFamily: 'var(--serif)', fontWeight: 400, fontSize: '1.15rem',
              color: 'var(--text-primary)', margin: '0 0 12px 0',
            }}>Edit prompt for this thread only?</h3>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: '0.88rem', fontWeight: 300,
              color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px 0',
            }}>
              This changes the system prompt in this conversation only. Future threads
              won't be affected, and updates to your prompt template won't reach this
              thread anymore.
            </p>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: '0.82rem', fontWeight: 300,
              color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 20px 0',
            }}>
              To edit the template for all future threads, go to{' '}
              <a href="/prompts" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Prompts</a>.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => { setShowPromptEditConfirm(false); setEditTarget(null); }}
                style={{
                  padding: '8px 20px', background: 'none',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  color: 'var(--text-secondary)', fontFamily: 'var(--sans)',
                  fontSize: '0.85rem', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={() => {
                  setShowPromptEditConfirm(false);
                  setShowEditOverlay(true);
                }}
                style={{
                  padding: '8px 20px', background: 'none',
                  border: '1px solid var(--accent)', borderRadius: '6px',
                  color: 'var(--accent)', fontFamily: 'var(--sans)',
                  fontSize: '0.85rem', cursor: 'pointer',
                }}
              >Edit for this thread</button>
            </div>
          </div>
        </div>
      )}

      {showEditOverlay && editTarget && (
        <NodeFormModal
          title="Edit Text"
          onClose={() => { setShowEditOverlay(false); setEditTarget(null); }}
          nodeFormProps={{
            editMode: true,
            nodeId: editTarget.id,
            initialContent: editTarget.content,
            initialPrivacyLevel: editTarget.privacy_level,
            initialAiUsage: editTarget.ai_usage,
            detachPrompt: !!editTarget.context_artifacts?.prompt,
            hasGeneratedTts: !!editTarget.has_tts,
            // Drives the "apply to replies too?" choice on a privacy /
            // AI-usage change (same test as the delete dialog).
            hasChildren: !!(
              editTarget.child_count > 0
              || (editTarget.children && editTarget.children.length > 0)
            ),
            onSuccess: handleEditSuccess,
          }}
        />
      )}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        mode="single"
        hasChildren={!!(deleteTarget && (
          deleteTarget.child_count > 0
          || (deleteTarget.children && deleteTarget.children.length > 0)
        ))}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
      <DeleteConfirmDialog
        open={!!pendingPromptDelete}
        mode="prompt"
        listedIn={
          threadRoot.privacy_level === "public" ? "your public page" : "your Log"
        }
        onClose={() => setPendingPromptDelete(null)}
        onConfirm={handleConfirmPromptDelete}
      />
      {replyTarget && (
        <NodeFormModal
          title="Reply"
          onClose={() => setReplyTarget(null)}
          nodeFormProps={{
            parentId: replyTarget.id,
            hidePowerFeatures: !craftMode,
            onSuccess: (data) => {
              setReplyTarget(null);
              navigate(`/node/${data.id}`);
            },
          }}
        />
      )}
    </div>
  );
}

export default NodeDetail;
